// OfficeArtUndName.tsx — die zwei Fragen vor einem neuen Office-Dokument:
// „Word oder Excel?" und „Wie soll es heißen?".
//
// Ein Bauteil für beide Stellen (User 28.09.2026): das „+“-Menü im App-Header
// (NeuAnlageDialog, dort mit „Datei hochladen" als drittem Weg) und
// „Neues Dokument" am Vorgang (NeuesOfficeDokumentDialog). Optik aus
// neuAnlage.css, Scope `.kvp-rd-scrim`.

import * as React from "react";
import { ArrowUpload20Regular, DocumentText20Regular, Table20Regular } from "@fluentui/react-icons";
import { OFFICE_ARTEN, nameMitEndung, type OfficeArt } from "./officeDokument";
import "./neuAnlage.css";

const SYMBOL: Record<OfficeArt, React.ReactElement> = {
  docx: <DocumentText20Regular />,
  xlsx: <Table20Regular />,
};

/** Wert der dritten Kachel im „+“-Menü: der bisherige Upload-Weg. */
export const DATEI_HOCHLADEN = "datei" as const;
export type DokumentWeg = OfficeArt | typeof DATEI_HOCHLADEN;

/**
 * Word | Excel als Kacheln (Radiogruppe). `mitHochladen` hängt „Datei
 * hochladen" als dritte Kachel an — damit im „+“-Menü nichts verloren geht,
 * was es vorher gab.
 */
export function OfficeArtKacheln({
  gewaehlt,
  onWaehlen,
  mitHochladen = false,
  mitOffice = true,
}: {
  gewaehlt: DokumentWeg | null;
  onWaehlen: (weg: DokumentWeg) => void;
  mitHochladen?: boolean;
  /** `false`, wenn der Server keinen Office-Editor hat — dann nur Hochladen. */
  mitOffice?: boolean;
}) {
  const kacheln: Array<{ weg: DokumentWeg; label: string; hinweis: string; ico: React.ReactElement }> = [
    ...(mitOffice ? OFFICE_ARTEN.map((a) => ({ weg: a.art, label: a.label, hinweis: a.hinweis, ico: SYMBOL[a.art] })) : []),
    ...(mitHochladen
      ? [{ weg: DATEI_HOCHLADEN, label: "Datei hochladen", hinweis: "Vom Rechner, mit Kategorie", ico: <ArrowUpload20Regular /> }]
      : []),
  ];
  return (
    <div className="na-kacheln" role="radiogroup" aria-label="Art des Dokuments">
      {kacheln.map((k) => (
        <button
          key={k.weg}
          type="button"
          role="radio"
          aria-checked={gewaehlt === k.weg}
          className={"na-kachel" + (gewaehlt === k.weg ? " an" : "")}
          onClick={() => onWaehlen(k.weg)}
        >
          <span className="na-kachel-ico" aria-hidden>
            {k.ico}
          </span>
          <span className="na-kachel-text">
            {k.label}
            <span className="na-kachel-sub">{k.hinweis}</span>
          </span>
        </button>
      ))}
    </div>
  );
}

/** Namensfeld mit fester Endung dahinter; Enter legt an. */
export function OfficeNameFeld({
  art,
  name,
  onName,
  onEnter,
  autoFocus = false,
}: {
  art: OfficeArt;
  name: string;
  onName: (name: string) => void;
  onEnter?: () => void;
  autoFocus?: boolean;
}) {
  const id = React.useId();
  const endgueltig = nameMitEndung(name, art);
  return (
    <div>
      <div className="na-suche na-name">
        <input
          id={id}
          type="text"
          aria-label="Name des Dokuments"
          placeholder={art === "docx" ? "z. B. Angebot Betriebshaftpflicht" : "z. B. Fahrzeugliste 2026"}
          value={name}
          maxLength={200}
          autoFocus={autoFocus}
          onChange={(e) => onName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && onEnter) {
              e.preventDefault();
              onEnter();
            }
          }}
        />
        <span className="na-name-endung" aria-hidden>
          .{art}
        </span>
      </div>
      <p className="na-hinweis" style={{ margin: "8px 2px 0" }} aria-live="polite">
        {endgueltig ? (
          <>
            Wird gespeichert als <strong>{endgueltig}</strong> und danach im Editor geöffnet.
          </>
        ) : (
          "Die Endung kommt automatisch dazu."
        )}
      </p>
    </div>
  );
}

/** Beides zusammen — für den schlanken Dialog am Vorgang. */
export function OfficeArtUndName({
  art,
  onArt,
  name,
  onName,
  onEnter,
}: {
  art: OfficeArt | null;
  onArt: (art: OfficeArt) => void;
  name: string;
  onName: (name: string) => void;
  onEnter?: () => void;
}) {
  return (
    <>
      <section className="na-abschnitt">
        <h3 className="na-frage">Word oder Excel?</h3>
        <OfficeArtKacheln gewaehlt={art} onWaehlen={(w) => w !== DATEI_HOCHLADEN && onArt(w)} />
      </section>
      {art && (
        <section className="na-abschnitt">
          <h3 className="na-frage">Name</h3>
          <OfficeNameFeld key={art} art={art} name={name} onName={onName} onEnter={onEnter} autoFocus />
        </section>
      )}
    </>
  );
}
